import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Bookmark, ExternalLink, LogOut, Menu, Moon, Sun, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { t, lang, setLang, type Lang } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useSavedViews } from "@/hooks/useSavedViews";
import { writeFilters } from "@/hooks/useUrlFilters";
import { useNavEntries, type NavEntry } from "./Sidebar";
import type { SavedView } from "@/lib/types";

/**
 * Bereiche mit festem Platz in der Bottom-Bar. Alles andere (Zeitstrahl,
 * Metabase, Admin-Bereiche, gespeicherte Sichten, Sprache/Theme/Abmelden)
 * liegt hinter „Mehr". Vier Tabs + „Mehr" = fünf Ziele à ≥ 64px auf 375px.
 */
const PRIMARY = ["/uebersicht", "/logs", "/traces", "/anlagen"];

const tabCls =
  "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 transition-colors";

export function MobileNav() {
  const entries = useNavEntries();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { username, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const { views } = useSavedViews();

  // Sheet schließt bei jeder Navigation — auch bei Saved-View-Sprüngen (Query ändert sich).
  useEffect(() => setOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const primary = PRIMARY.map((p) => entries.find((e) => e.to === p)).filter(
    (e): e is NavEntry => !!e,
  );
  const secondary = entries.filter((e) => !PRIMARY.includes(e.to));
  // „Mehr" leuchtet, wenn die aktuelle Route hinter dem Sheet liegt.
  const moreActive = secondary.some((e) => !e.external && location.pathname.startsWith(e.to));

  const openView = (v: SavedView) => {
    const params = writeFilters(v.filters);
    navigate({ pathname: "/logs", search: params.toString() ? `?${params}` : "" });
  };

  const initials = (username ?? "?").slice(0, 2).toUpperCase();

  return (
    <>
      <nav
        aria-label={t("Navigation")}
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card pb-safe md:hidden"
      >
        <div className="flex h-14 items-stretch">
          {primary.map((e) => (
            <NavLink
              key={e.to}
              to={e.to}
              className={({ isActive }) =>
                cn(tabCls, isActive ? "text-primary" : "text-muted-foreground")
              }
            >
              <e.icon className="h-5 w-5" />
              <span className="max-w-full truncate text-[10px] font-medium leading-none">
                {e.label}
              </span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={cn(tabCls, open || moreActive ? "text-primary" : "text-muted-foreground")}
          >
            <Menu className="h-5 w-5" />
            <span className="text-[10px] font-medium leading-none">{t("Mehr")}</span>
          </button>
        </div>
      </nav>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-[1px] md:hidden"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-label={t("Mehr")}
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-xl border-t border-border bg-card pb-safe shadow-2xl md:hidden"
          >
            <div className="flex items-center justify-between py-2 pl-4 pr-2">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-medium text-accent-foreground">
                  {initials}
                </span>
                <span className="truncate text-muted-foreground">
                  {t("Angemeldet als {name}", { name: username ?? "?" })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("Schließen")}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-2 pb-2">
              {secondary.map((entry) =>
                entry.external ? (
                  <a
                    key={entry.to}
                    href={entry.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={sheetRowCls}
                  >
                    <entry.icon className="h-5 w-5 text-muted-foreground" />
                    <span className="flex-1">{entry.label}</span>
                    <ExternalLink className="h-4 w-4 opacity-50" />
                  </a>
                ) : (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    className={({ isActive }) =>
                      cn(sheetRowCls, isActive && "bg-accent text-accent-foreground font-medium")
                    }
                  >
                    <entry.icon className="h-5 w-5 text-muted-foreground" />
                    <span className="flex-1">{entry.label}</span>
                  </NavLink>
                ),
              )}

              {views.length > 0 ? (
                <>
                  <div className="mt-2 px-3 pb-1 pt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("Gespeicherte Sichten")}
                  </div>
                  {views.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => openView(v)}
                      className={cn(sheetRowCls, "w-full text-left")}
                    >
                      <Bookmark className="h-5 w-5 text-muted-foreground" />
                      <span className="flex-1 truncate">{v.name}</span>
                    </button>
                  ))}
                </>
              ) : null}

              <div className="mt-2 border-t border-border pt-2">
                <div className="flex min-h-12 items-center justify-between px-3 py-1 text-sm">
                  <span className="text-muted-foreground">{t("Sprache")}</span>
                  <div className="flex gap-1">
                    {(["de", "en", "fr"] as Lang[]).map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setLang(l)}
                        aria-pressed={lang === l}
                        className={cn(
                          "h-9 min-w-11 rounded-md border px-2 text-xs uppercase",
                          lang === l
                            ? "border-primary bg-accent font-medium text-accent-foreground"
                            : "border-border text-muted-foreground",
                        )}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={toggle} className={cn(sheetRowCls, "w-full text-left")}>
                  {dark ? (
                    <Sun className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Moon className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="flex-1">{t("Theme umschalten")}</span>
                </button>

                <button type="button" onClick={logout} className={cn(sheetRowCls, "w-full text-left")}>
                  <LogOut className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1">{t("Abmelden")}</span>
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

// ≥ 48px hohe Zeilen — Daumen statt Mauszeiger.
const sheetRowCls =
  "flex min-h-12 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted/40";
